// Pagina episodio "AlphaFrame Lab": lo speech dell'episodio (fonte QuantFinance) come
// articolo completo. Video YouTube aggiunto quando pubblicato (videoId in labEpisodes.js).
import React, { useContext } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Seo from "../components/Seo";
import { LangContext } from "../lang/LangProvider";
import { LAB_EPISODES, getLabEpisode } from "../data/labEpisodes";
import LAB_CONTENT from "../data/labContent.json";
import YouTubeEmbed from "../components/YouTubeEmbed";
import "./Series.css";

function L(lang, it, en) { return lang === "en" ? en : it; }

export default function LabEpisode() {
  const { lang } = useContext(LangContext);
  const { slug } = useParams();
  const ep = getLabEpisode(slug);
  const content = LAB_CONTENT[slug] && LAB_CONTENT[slug][lang === "en" ? "en" : "it"];

  if (!ep || !content) return <Navigate to={`/${lang}/series/lab`} replace />;

  const title = L(lang, ep.titleIt, ep.titleEn);
  const desc = L(lang, ep.descIt, ep.descEn);
  const prev = LAB_EPISODES.find((e) => e.n === ep.n - 1 && e.slug);
  const next = LAB_EPISODES.find((e) => e.n === ep.n + 1 && e.slug);

  return (
    <>
      <Seo
        title={`AlphaFrame Lab — Ep. ${ep.n}: ${title}`}
        description={desc}
        path={`/${lang}/series/lab/${ep.slug}`}
        image="/og/cover-tools.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description: desc,
          inLanguage: lang,
          isPartOf: { "@type": "Series", name: "AlphaFrame Lab" },
          author: { "@type": "Organization", name: "AlphaFrame Analytics" },
        }}
      />
      <div className="series-hub lab-episode">
        <p className="series-eyebrow">
          <Link to={`/${lang}/series`}>← {L(lang, "Le Serie", "Series")}</Link>
          {" · "}
          <Link to={`/${lang}/series/lab`}>AlphaFrame Lab</Link>
        </p>
        <header className="series-head">
          <h1>
            {L(lang, "Ep.", "Ep.")} {ep.n} — {title}
          </h1>
          <p className="series-intro">{desc}</p>
          <p className="track-mktg-label">
            {L(
              lang,
              "Comunicazione di marketing a scopo informativo/educativo. Non è consulenza, raccomandazione, sollecitazione né promessa di rendimento; il capitale è a rischio. Il servizio di consulenza in materia di investimenti non è attualmente avviato e non è prestata gestione di portafogli: i modelli descritti sono ricerca quantitativa proprietaria, non un prodotto o servizio sottoscrivibile. I dati citati sono in-sample/backtest su dati storici (i rendimenti passati o simulati non sono indicativi di quelli futuri).",
              "Marketing communication for informational/educational purposes. Not advice, a recommendation, a solicitation or a return promise; capital is at risk. The investment advice service has not yet commenced and no portfolio management is provided: the models described are proprietary quantitative research, not a product or subscribable service. Figures quoted are in-sample/backtest on historical data (past or simulated returns are not indicative of future ones)."
            )}
          </p>
        </header>

        {/* Nessuna promessa di video: il player compare solo se l'episodio esiste su YouTube
            (videoId valorizzato in labEpisodes.js). Decisione 26/09/2026. */}
        {ep.videoId && <YouTubeEmbed videoId={ep.videoId} title={title} />}

        <div className="lab-howto">
          <strong>{L(lang, "Come leggere i numeri di questo episodio", "How to read the figures in this episode")}</strong>
          <p>
            {L(lang,
              "È un approccio algoritmico: i modelli vengono addestrati, testati e validati su blocchi separati di dati storici (train / test / validation), e alcuni partono da un anno specifico. Le cifre citate (CAGR, drawdown, alpha, Sharpe) sono risultati di backtest simulati in-sample su dati storici di mercato — alcuni al netto della tassa del 26% e dei costi, altri al lordo, come indicato di volta in volta. NON sono un track record né una previsione: i rendimenti passati o simulati non sono indicativi di quelli futuri. I tre modelli principali NON hanno ancora superato una validazione fuori campione: la prova vera è il test out-of-sample, con verdetto a data fissata (dic 2026 / giu 2027).",
              "This is an algorithmic approach: models are trained, tested and validated on separate blocks of historical data (train / test / validation), and some start from a specific year. The figures quoted (CAGR, drawdown, alpha, Sharpe) are in-sample backtest results on historical market data — some net of the Italian 26% tax and costs, some gross, as indicated case by case. They are NOT a track record or a forecast: past or simulated returns are not indicative of future ones. The three main models have NOT yet passed out-of-sample validation: the real test is the out-of-sample one, with a dated verdict (Dec 2026 / Jun 2027).")}
          </p>
        </div>

        <article className="lab-body">
          {content.map((sec, i) => (
            <section key={i}>
              <h2>{sec.title}</h2>
              {sec.paras.map((p, j) => (
                <p key={j}>{p}</p>
              ))}
            </section>
          ))}
        </article>

        {ep.charts && ep.charts.length > 0 && (
          <section className="lab-charts">
            <h2>{L(lang, "Grafici", "Charts")}</h2>
            <div className="lab-charts-grid">
              {ep.charts.map((c, i) => {
                const src = `/assets/lab/${c.file}${c.loc ? `_${lang === "en" ? "EN" : "IT"}` : ""}.png`;
                return (
                  <figure key={i} className="lab-chart">
                    <img src={src} alt={L(lang, c.capIt, c.capEn)} loading="lazy" />
                    <figcaption>{L(lang, c.capIt, c.capEn)}</figcaption>
                  </figure>
                );
              })}
            </div>
            <p className="lab-charts-note">
              {L(lang,
                "Grafici da backtest simulati in-sample. Periodo di riferimento: circa 2006-2025 (varia per modello; alcuni partono da un anno specifico). Fonte dati: prezzi storici di mercato (Adjusted Close, total return) e serie macro FRED. A fini illustrativi: leggere la forma e il rischio, non i livelli esatti come previsione; i rendimenti passati o simulati non sono indicativi di quelli futuri. Universo per classi/settori, nessun titolo specifico né allocazione corrente.",
                "Charts from in-sample backtests. Reference period: about 2006-2025 (varies by model; some start from a specific year). Data source: historical market prices (Adjusted Close, total return) and FRED macro series. For illustration: read the shape and the risk, not the exact levels as a forecast; past or simulated returns are not indicative of future ones. Universe by asset class/sector, no specific securities and no current allocation.")}
            </p>
          </section>
        )}

        {ep.table && (
          <section className="lab-table-sec">
            <div className="lab-table-scroll">
              <table className="lab-table">
                <thead>
                  <tr>{(lang === "en" ? ep.table.headEn : ep.table.headIt).map((h, i) => <th key={i}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {ep.table.rows.map((r, i) => (
                    <tr key={i}>{r.map((cell, j) => <td key={j} className={j === 0 ? "lab-td-label" : ""}>{cell}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="lab-charts-note">{L(lang, ep.table.captionIt, ep.table.captionEn)}</p>
          </section>
        )}

        <nav className="lab-nav">
          {prev && (
            <Link to={`/${lang}/series/lab/${prev.slug}`}>← Ep. {prev.n}: {L(lang, prev.titleIt, prev.titleEn)}</Link>
          )}
          {next && (
            <Link to={`/${lang}/series/lab/${next.slug}`}>Ep. {next.n}: {L(lang, next.titleIt, next.titleEn)} →</Link>
          )}
        </nav>

        <p className="series-disclaimer">
          {L(
            lang,
            "Comunicazione di marketing a scopo informativo/educativo — non consulenza personalizzata, raccomandazione o sollecitazione all'investimento, e nessuna promessa di rendimento. Il capitale è a rischio e sono possibili perdite. Il servizio di consulenza in materia di investimenti non è attualmente avviato e non è prestata gestione di portafogli: i modelli qui descritti sono ricerca quantitativa proprietaria, non un prodotto o servizio sottoscrivibile. I dati sono in-sample/da backtest su dati storici, al netto della tassa del 26% e dei costi dove specificato, con i limiti tipici (possibile adattamento ai dati); le performance passate o simulate non sono indicative di quelle future. Universo descritto per classi/settori: nessun titolo specifico né allocazione corrente.",
            "Marketing communication for informational/educational purposes — not personalised advice, a recommendation or a solicitation to invest, and no return promise. Capital is at risk and losses are possible. The investment advice service has not yet commenced and no portfolio management is provided: the models described here are proprietary quantitative research, not a product or subscribable service. Figures are in-sample/backtest on historical data, net of the Italian 26% tax and costs where specified, with the usual limits (possible overfitting); past or simulated performance is not indicative of future results. Universe described by asset class/sector: no specific securities and no current allocations."
          )}
        </p>
      </div>
    </>
  );
}
